import HBAvatar from "@/components/Avatar";
import OfficialAccountTag from "@/components/Avatar/Official";
import HBIcon from "@/components/HBIcon";
import { MuseTag } from "@/components/Muse";
import { UserList } from "@/components/User";
import UserCardPopover from "@/components/UserCard/Popover";
import useAppStore from "@/context/store";
import { getBoardMembers } from "@/services";
import { isCollabBoardMember } from "@/utils/board";
import { useEnableMultiBoard } from "@/utils/hooks/useMultiBoard";
import useReq from "@/utils/useReq";
import { useMemoizedFn, useUpdateEffect } from "ahooks";
import { Dropdown } from "antd";
import classNames from "classnames";
import { useMemo } from "react";
import { BoardMemberMngDialog } from "../MemberMng";
import styles from "./index.module.less";

interface IBoardMembersProps {
  board: IBoardInfo;
  isManager?: boolean;
  onClick?: () => void;
  showMaxNum?: number;
  members?: IUser[];
  mutateMembers?: (members: IUser[]) => void;
  onUpdateBoard?: (board: IBoardInfo) => void;
  onRemoveMember?: (user: IUser) => void;
  onExitBoard?: () => void;
  showUserOwner?: boolean;
}

/**
 * 画板成员展示（画板详情页）
 * @param param0
 * @returns
 */
export function BoardMembers({
  board,
  onClick: _onClick,
  showMaxNum,
  onUpdateBoard,
  members: _members,
  onRemoveMember,
  onExitBoard,
  showUserOwner,
}: IBoardMembersProps) {
  const user = useAppStore((s) => s.user);

  const { enable } = useEnableMultiBoard();

  const isMember = useMemo(() => {
    return isCollabBoardMember(board, user);
  }, [board, user]);

  const showAdd =
    board?.user_id === user?.user_id && !board?.extra?.is_creation;

  const onClick = useMemoizedFn(() => {
    if (!user || !isMember || board?.extra?.is_creation) {
      return;
    }

    BoardMemberMngDialog.open({
      board,
      onUpdateBoard,
      onRemoveMember,
      onExitBoard,
    });
    _onClick && _onClick();
  });

  const { data: members = _members, mutate: mutateMembers } = useReq(
    async () => {
      // 没有协作成员
      if (!board?.extra?.collab_members) return [];

      if (_members) return _members;

      // 获取所有协作成员
      const res = await getBoardMembers({ board_id: board.board_id });
      return res.data || [];
    },
    {
      refreshDeps: [board?.board_id],
    }
  );

  useUpdateEffect(() => {
    mutateMembers(_members);
  }, [_members]);

  const content = useMemo(() => {
    const showMembers =
      members?.length > showMaxNum
        ? members?.slice(0, showMaxNum - 1)
        : members;

    return (
      <div
        className={styles.container}
        onClick={onClick}
        data-gd-click="button_click"
        data-button-name="成员管理"
      >
        {!members?.length && (
          <a className={styles.creator} href={`/user/${board?.user?.urlname}`}>
            <HBAvatar user={board?.user} shape="circle" size={34} withBorder />
          </a>
        )}

        {/* 展示showMaxNum-1 个成员 */}

        {showMembers?.map((member, index) => {
          return (
            // <Tooltip title={member.username} placement="bottom" >
            <div className={styles.member} key={index}>
              <HBAvatar user={member} shape="circle" size={34} withBorder />
            </div>
            // </Tooltip>
          );
        })}

        {members?.length > showMaxNum && (
          <span className={styles.more}>
            <span>{members?.length - showMaxNum + 1}</span>
          </span>
        )}

        {showAdd && enable && (
          <span className={styles.manager}>
            <HBIcon type="hb_add_member" />
          </span>
        )}
      </div>
    );
  }, [members, showAdd, board]);

  if (!board?.user && !members?.length) {
    return null;
  }

  const isOwner = user?.user_id === board?.user?.user_id;

  if (
    showUserOwner &&
    members?.length <= 1 &&
    (!isOwner || board?.extra?.is_creation || !enable)
  ) {
    // 画板详情页下，单人画板需要展示单个用户的头像与名字等信息
    return <BoardOwner board={board} />;
  }

  if (isMember) {
    return content;
  }

  return (
    <Dropdown
      {...(isMember ? { visible: false } : null)}
      overlayClassName={styles.overlay}
      align={{ offset: [0, 18] }}
      trigger={["click"]}
      overlay={
        <>
          <div className={styles.title}>成员列表</div>
          <UserList
            board={board}
            selecteds={[]}
            maxHeight={400}
            renderAction={() => null}
            invitedUsers={members}
          />
        </>
      }
    >
      {content}
    </Dropdown>
  );
}

export function BoardMemberInCard({
  className,
  board,
  showMaxNum,
}: {
  board: IBoardInfo;
  className?: string;
  showMaxNum?: number;
}) {
  if (!(board?.members?.length > 1)) {
    return null;
  }

  return (
    <div className={classNames(styles.container, styles.card, className)}>
      {board.members?.map((member, index) => {
        if (
          board?.extra?.collab_member_count > showMaxNum &&
          index >= showMaxNum - 1
        ) {
          return null;
        }
        return (
          <HBAvatar
            key={member.user_id}
            className={styles.member}
            user={member}
            size={24}
            shape="circle"
            withBorder
          />
        );
      })}
      {/* 如果成员数量大于showMaxNum，则显示成员数量 */}
      {board?.extra?.collab_member_count > showMaxNum && (
        <span className={styles.more}>
          <span>{board?.extra?.collab_member_count - showMaxNum + 1}</span>
        </span>
      )}
    </div>
  );
}

function BoardOwner({ board }: { board: IBoardInfo }) {
  return (
    <UserCardPopover record={{ ...board?.user, following: board?.following }}>
      <a className={styles.creator} href={`/user/${board?.user?.urlname}`}>
        <HBAvatar user={board?.user} shape="circle" size={32} withBorder />

        <span className={styles.name} style={{ fontSize: "16px" }}>
          {board?.user?.username}
        </span>
        <OfficialAccountTag user={board?.user} />
        {board?.user?.extra?.is_museuser && <MuseTag user={board?.user} />}
      </a>
    </UserCardPopover>
  );
}
